import React, { useEffect, useState } from "react";
import axios from "axios";

// ============================================================================
// PublicTrackRecord — anonymised, unauthenticated leaderboard of completed
// Trade Ideas.  RC2.5 · Institutional transparency.
// Never fabricates statistics.
// ============================================================================
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicTrackRecord() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regime, setRegime] = useState("");
  const [sector, setSector] = useState("");
  const [days, setDays] = useState(180);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        days: String(days),
        limit: "100",
        ...(regime ? { regime } : {}),
        ...(sector ? { sector } : {}),
      });
      const r = await axios.get(`${API}/public/track-record?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [regime, sector, days]);   // eslint-disable-line

  const statBlock = (label, s, testId) => {
    const collecting = s?.status === "collecting_evidence";
    return (
      <div className="border border-[color:var(--border-default)] p-3" data-testid={testId}>
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
        {collecting ? (
          <div className="text-[11px] font-mono-data text-gold mt-2">Collecting historical evidence.</div>
        ) : (
          <>
            <div className="font-mono-data text-2xl text-white mt-1">
              {s?.win_rate_pct == null ? "—" : `${s.win_rate_pct}%`}
            </div>
            <div className="text-[10px] font-mono-data text-[color:var(--text-muted)]">
              n {s?.n ?? 0} · avg R:R {s?.avg_risk_reward ?? "—"} · hold {s?.avg_holding_days ?? "—"}d
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)] text-white p-6" data-testid="public-track-record">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="border-b border-[color:var(--border-default)] pb-3">
          <div className="text-[10px] font-mono-data text-gold tracking-widest">◆ TRADINGGODAI · PUBLIC TRACK RECORD</div>
          <h1 className="font-mono-data text-2xl mt-1">Institutional Signal Intelligence — Live Performance</h1>
          <p className="text-[11px] font-mono-data text-[color:var(--text-muted)] mt-1">
            Anonymised, read-only. Only completed setups are shown. Never fabricates statistics.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono-data">
          <span className="text-[color:var(--text-muted)]">WINDOW</span>
          {[30, 90, 180, 365].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`ptr-window-${d}`}
              className={`px-2 py-0.5 border ${days === d ? "border-white text-white" : "border-[color:var(--border-default)] text-[color:var(--text-secondary)]"}`}
            >
              {d}D
            </button>
          ))}
          <span className="text-[color:var(--text-muted)] ml-3">REGIME</span>
          <select value={regime} onChange={e => setRegime(e.target.value)} data-testid="ptr-regime-filter"
            className="bg-transparent border border-[color:var(--border-default)] px-2 py-0.5 text-[11px]">
            <option value="">ALL</option>
            <option value="bull">Bull</option>
            <option value="sideways">Sideways</option>
            <option value="bear">Bear</option>
          </select>
          <span className="text-[color:var(--text-muted)] ml-3">SECTOR</span>
          <input type="text" value={sector} onChange={e => setSector(e.target.value)} placeholder="e.g. Technology"
            data-testid="ptr-sector-filter"
            className="bg-transparent border border-[color:var(--border-default)] px-2 py-0.5 text-[11px] w-40" />
        </div>

        {/* Stats grid */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {statBlock("STRONG BUY", data.stats?.strong_buy, "ptr-stat-strong-buy")}
            {statBlock("BUY", data.stats?.buy, "ptr-stat-buy")}
            {statBlock("WATCH", data.stats?.watch, "ptr-stat-watch")}
            {statBlock("OVERALL", data.stats?.overall, "ptr-stat-overall")}
          </div>
        )}

        {/* Rows table */}
        <div className="border border-[color:var(--border-default)]">
          <div className="grid grid-cols-[100px,80px,60px,70px,90px,60px,80px,60px] gap-2 px-3 py-2 text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] border-b border-[color:var(--border-default)]">
            <span>DATE</span><span>SYMBOL</span><span>DIR</span><span>SCORE</span><span>REC</span><span>W/L</span><span>RET%</span><span>DAYS</span>
          </div>
          {loading && !data && <div className="p-3 text-[11px] text-[color:var(--text-muted)]">Loading…</div>}
          {data && data.rows.length === 0 && (
            <div className="p-3 text-[11px] font-mono-data text-gold" data-testid="ptr-empty">
              Collecting historical evidence.
            </div>
          )}
          {data && data.rows.map((r, i) => (
            <div key={i} data-testid={`ptr-row-${i}`} className="grid grid-cols-[100px,80px,60px,70px,90px,60px,80px,60px] gap-2 px-3 py-1.5 text-[11px] font-mono-data border-b border-[color:var(--border-default)] last:border-0">
              <span className="text-white">{r.date}</span>
              <span className="text-white">{r.symbol}</span>
              <span className={r.direction === "LONG" ? "text-bull" : "text-bear"}>{r.direction}</span>
              <span className="text-white">{r.signal_score ?? "—"}</span>
              <span className={r.recommendation === "strong_buy" ? "text-bull" : r.recommendation === "avoid" ? "text-bear" : "text-gold"}>
                {(r.recommendation || "").replace("_", " ").toUpperCase()}
              </span>
              <span className={r.outcome === "win" ? "text-bull" : r.outcome === "loss" ? "text-bear" : "text-gold"}>
                {r.outcome ? r.outcome.charAt(0).toUpperCase() : "—"}
              </span>
              <span className={r.return_pct >= 0 ? "text-bull" : "text-bear"}>
                {r.return_pct == null ? "—" : `${r.return_pct > 0 ? "+" : ""}${r.return_pct}%`}
              </span>
              <span className="text-[color:var(--text-secondary)]">{r.holding_days ?? "—"}</span>
            </div>
          ))}
        </div>

        {data && (
          <div className="text-[10px] font-mono-data text-[color:var(--text-muted)]">
            {data.disclaimer}
          </div>
        )}
      </div>
    </div>
  );
}
